<template>
    <div>
        <div v-if="!fetchingSpaceUsers" class="d-flex flex-wrap align-center">
            <SpaceInviteDialog v-if="spaceUsers.length">
                <template v-slot:activator>
                    <v-btn color="primary">+ user</v-btn>
                </template>
            </SpaceInviteDialog>
            <v-btn color="white" class="ml-2" :disabled="!spaceUsers.length" :loading="csvExporting" @click="csvExport">Export to CSV</v-btn>
            <v-spacer />
            <v-text-field
                class="mx-2"
                v-show="spaceUsers.length"
                placeholder="Search for a user..."
                autofocus
                prepend-inner-icon="mdi-magnify "
                v-model="userSearch"
                solo
                flat
                hide-details
                dense
                clearable>
                <template v-slot:append>
                    <v-menu offset-y :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" small class="mr-n2" :disabled="!spaceUsers.length">
                                <v-icon left>mdi-tune-variant</v-icon>
                                <span>Filters</span>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <div v-for="(filter, role) in roleFilters" :key="role">
                                <v-sheet rounded class="py-2 px-4 my-1">
                                    <v-switch v-model="filter.enabled" inset dense hide-details class="pa-0 ma-0">
                                        <template v-slot:label>
                                            <div class="d-flex align-center">
                                                <v-icon left small>{{ spaceRole(role).icon }}</v-icon>
                                                <span>{{ capitalize(spaceRole(role).label) }}</span>
                                            </div>
                                        </template>
                                    </v-switch>
                                </v-sheet>
                            </div>
                            <v-divider class="my-2" />
                            <v-sheet rounded class="py-2 px-4 my-1">
                                <v-switch v-model="showInheritedRoles" inset dense hide-details class="pa-0 ma-0">
                                    <template v-slot:label>
                                        <div class="d-flex align-center">
                                            <v-icon left small>mdi-chevron-double-down</v-icon>
                                            <span>Inherited roles</span>
                                        </div>
                                    </template>
                                </v-switch>
                            </v-sheet>
                        </v-list>
                    </v-menu>
                </template>
            </v-text-field>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" @click="$store.dispatch('spaceStore/fetchSpaceUsers', $route.params.sid)" text icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>refresh</span>
            </v-tooltip>
        </div>

        <div v-if="!fetchingSpaceUsers">
            <v-data-table
                v-if="spaceUsers.length"
                :headers="userHeaders"
                :items="filteredSpaceUsers"
                item-key="uid"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                :expanded.sync="expanded"
                show-expand
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.full_name`]="{ item }">
                    <div class="font-weight-medium secondary--text py-2">{{ item.full_name || '-' }}</div>
                </template>
                <template v-slot:[`item.email`]="{ item }">
                    <div class="py-2">{{ item.email || '-' }}</div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <div class="py-2">
                        <v-chip v-if="item.is_active === true" outlined label small>
                            <v-icon left x-small color="success">mdi-circle</v-icon>
                            <span>Active</span>
                        </v-chip>
                        <v-chip v-else-if="item.is_active === false" outlined label small>
                            <v-icon left x-small color="error">mdi-circle</v-icon>
                            <span>Inactive</span>
                        </v-chip>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.display_role`]="{ item }">
                    <v-tooltip v-if="item.reason === 'organization_manager'" bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" small color="secondary" class="mr-2">mdi-chevron-double-down</v-icon>
                        </template>
                        <span>Inherited from organization manager</span>
                    </v-tooltip>
                    <v-chip outlined label small>
                        <v-icon left small>{{ spaceRole(item.display_role).icon }}</v-icon>
                        <span>{{ capitalize(spaceRole(item.display_role).label) }}</span>
                    </v-chip>
                </template>
                <template v-slot:[`item.joined_at`]="{ item }">
                    <span v-if="item.joined_at" class="text-no-wrap">{{ item.joined_at | dateTimeToHuman }}</span>
                    <span v-else>&mdash;</span>
                </template>
                <template v-slot:[`item.invited_by`]="{ item }">
                    <span>{{ item.invited_by || '\u2014' }}</span>
                </template>
                <template v-slot:[`item.invited_at`]="{ item }">
                    <span v-if="item.invited_at" class="text-no-wrap">{{ item.invited_at | dateTimeToHuman }}</span>
                    <span v-else>&mdash;</span>
                </template>
                <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
                    <v-btn v-if="item.role === 'MEMBER' && instanceRoles(item).length" icon small @click.stop="expand(!isExpanded)">
                        <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </v-btn>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="pa-0">
                        <div class="grey lighten-4 pl-15">
                            <v-row no-gutters>
                                <v-col cols="6" class="px-4 py-3"><b>Instance</b></v-col>
                                <v-col cols="4" class="px-4 py-3"><b>Role</b></v-col>
                                <v-col cols="2" class="px-4 py-3 text-right"><b>Actions</b></v-col>
                            </v-row>
                            <v-divider />
                            <div v-for="instance in instanceRoles(item)" :key="`${instance.iid}-${instance.role_name}`">
                                <v-row no-gutters align="center">
                                    <v-col cols="6" class="px-4 py-2">{{ instance.Instance }}</v-col>
                                    <v-col cols="4" class="px-4 py-2">
                                        <v-chip outlined label small>
                                            <v-icon left small>{{ instanceRole(instance.role_name).icon }}</v-icon>
                                            {{ capitalize(instanceRole(instance.role_name).label) }}
                                        </v-chip>
                                    </v-col>
                                    <v-col cols="2" class="px-4 py-2 text-right">
                                        <v-menu left offset-x nudge-top="10">
                                            <template v-slot:activator="{ on }">
                                                <v-btn v-on="on" text color="secondary" small>
                                                    <v-icon>more_horiz</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list nav dense>
                                                <InstanceUserRoleDialog
                                                    :ucid="item.ucid.toString()"
                                                    :userName="item.full_name"
                                                    :email="item.email"
                                                    :instanceId="instance.iid.toString()"
                                                    :instanceName="instance.Instance"
                                                    :role="instance.role_name"
                                                    @role-updated="fetchUsers" />
                                                <v-divider class="my-2" />
                                                <UserActions
                                                    :ucid="item.ucid.toString()"
                                                    :userName="item.full_name"
                                                    :hierarchicalLevel="nuvolosObjectTypes.INSTANCE"
                                                    :instanceId="instance.iid.toString()"
                                                    :instanceName="instance.Instance"
                                                    :role="instance.role_name"
                                                    reason="explicit"
                                                    :spaceAdmin="false"
                                                    activatorType="listItem" />
                                            </v-list>
                                        </v-menu>
                                    </v-col>
                                </v-row>
                                <v-divider />
                            </div>
                        </div>
                    </td>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu
                        v-if="item.reason !== 'organization_manager'"
                        :value="openActionsMenuUid === item.uid"
                        @input="toggleActionsMenu(item.uid, $event)"
                        left
                        offset-x
                        min-width="220"
                        nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <TheSpaceAssignRolesDialog
                                v-if="isSpaceAdmin && item.role === 'MEMBER'"
                                :assignee="item.full_name"
                                :instanceData="getTargetInstancesForUserAssign(item)"
                                :spaceName="currentSpace && currentSpace.long_id"
                                :ucid="item.ucid.toString()"
                                :spaceRole="item.role"
                                @close-menu="closeActionsMenu" />
                            <v-divider v-if="item.role !== roleTypes.SPACE_ADMIN" class="my-2" />
                            <UserActions
                                :ucid="item.ucid.toString()"
                                :userName="item.full_name"
                                :hierarchicalLevel="nuvolosObjectTypes.SPACE"
                                :spaceName="currentSpace && currentSpace.long_id"
                                :role="item.role"
                                activatorType="listItem"
                                @close-menu="closeActionsMenu"></UserActions>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-data>
                    <span v-if="userSearch">No users found.</span>
                    <span v-else>No users found. Consider inviting new users via the "+ user" button above.</span>
                </template>
            </v-data-table>

            <div v-else>
                <v-alert text prominent type="info">
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">No users found</span>
                        <span>
                            Currently this space has no users. You can
                            <SpaceInviteDialog>
                                <template v-slot:activator>
                                    <v-btn small dark color="info" outlined>+ user</v-btn>
                                </template>
                            </SpaceInviteDialog>
                            new users.
                        </span>
                    </div>
                </v-alert>
            </div>
        </div>

        <div v-else>
            <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { capitalize } from 'lodash'
import jsonexport from 'jsonexport'
import { enumsData } from '@/mixins/enums'
import { instanceRole, spaceRole, userDisplayRole } from '@/utils'
import InstanceUserRoleDialog from '@/modules/instance/components/InstanceUserRoleDialog.vue'
import SpaceInviteDialog from '@/modules/space/components/SpaceInviteDialog.vue'
import TheSpaceAssignRolesDialog from '@/modules/space/components/TheSpaceAssignRolesDialog.vue'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(lifecycleErrors, permissionsErrors, spaceErrors)

const UserActions = () => import('@/components/UserActions.vue')

export default {
    name: 'SpaceUsers',
    mixins: [enumsData],
    components: { InstanceUserRoleDialog, TheSpaceAssignRolesDialog, UserActions, SpaceInviteDialog },
    data() {
        return {
            userSearch: '',
            openActionsMenuUcid: null,
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            csvExporting: false,
            expanded: [],
            openActionsMenuUid: null,
            showInheritedRoles: false,
            roleFilters: {
                OWNER: { enabled: true },
                SPACE_ADMIN: { enabled: true },
                MEMBER: { enabled: true }
            }
        }
    },
    computed: {
        ...mapState('spaceStore', ['currentSpace', 'spaceInstances', 'spaceUsers', 'fetchingSpaceUsers']),
        ...mapGetters('spaceStore', ['isSpaceAdmin']),
        userHeaders() {
            return [
                { text: '', value: 'data-table-expand', sortable: false, width: '48px' },
                { text: 'User', value: 'full_name', sortable: true, width: '260px' },
                { text: 'Email', value: 'email', sortable: true, width: '240px' },
                { text: 'Status', value: 'status', sortable: false, width: '120px' },
                { text: 'User type', value: 'display_role', sortable: true },
                { text: 'Joined', value: 'joined_at', sortable: true },
                { text: 'Invited by', value: 'invited_by', sortable: true },
                { text: 'Invited on', value: 'invited_at', sortable: true },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '120px' }
            ]
        },
        filteredSpaceUsers() {
            const term = this.userSearch?.trim().toLowerCase()
            return this.spaceUsers
                .map(user => ({ ...user, display_role: userDisplayRole(user) }))
                .filter(user => {
                    const matchesRole = this.roleFilters[user.display_role]?.enabled
                    const matchesInheritance = user.is_owner || this.showInheritedRoles || user.reason !== 'organization_manager'
                    const matchesSearch =
                        !term || `${user.full_name || ''} ${user.email || ''} ${spaceRole(user.display_role).label}`.toLowerCase().includes(term)
                    return matchesRole && matchesInheritance && matchesSearch
                })
                .sort((firstUser, secondUser) => {
                    const inheritanceOrder = Number(firstUser.reason === 'organization_manager') - Number(secondUser.reason === 'organization_manager')
                    return inheritanceOrder || (firstUser.full_name || '').localeCompare(secondUser.full_name || '', undefined, { sensitivity: 'base' })
                })
        }
    },
    methods: {
        capitalize,
        toggleActionsMenu(uid, isOpen) {
            this.openActionsMenuUid = isOpen ? uid : null
        },
        closeActionsMenu() {
            this.openActionsMenuUid = null
        },
        accessSource(reason) {
            if (reason === 'organization_manager') return 'Inherited from organization manager'
            if (reason === 'instance_access') return 'Direct instance access'
            return 'Explicit space role'
        },
        downloadCsv(content) {
            const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'space-users.csv')
            link.click()
            URL.revokeObjectURL(url)
        },
        async csvExport() {
            this.csvExporting = true
            try {
                const csvData = this.spaceUsers.map(user => ({
                    User: user.full_name || '',
                    Email: user.email || '',
                    Status: user.is_active ? 'Active' : 'Inactive',
                    'User type': this.spaceRole(userDisplayRole(user)).label,
                    'Space role': capitalize(this.spaceRole(user.role).label),
                    'Access source': this.accessSource(user.reason),
                    Joined: user.joined_at || '',
                    'Invited by': user.invited_by || '',
                    'Invited on': user.invited_at || '',
                    'Instance roles': this.instanceRoles(user)
                        .map(instance => `${instance.Instance}: ${capitalize(this.instanceRole(instance.role_name).label)}`)
                        .join('; ')
                }))
                const csv = await new Promise((resolve, reject) => {
                    jsonexport(csvData, {}, (error, output) => (error ? reject(error) : resolve(output)))
                })
                this.downloadCsv(csv)
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'Failed to export CSV')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            } finally {
                this.csvExporting = false
            }
        },
        instanceRole,
        instanceRoles(user) {
            return [...(user.instance_editor_roles || []), ...(user.instance_viewer_roles || []), ...(user.instance_observer_roles || [])]
        },
        getTargetInstancesForUserAssign(user) {
            const assignedRoles = this.instanceRoles(user).reduce((roles, instance) => {
                roles[instance.iid] = instance.role_name
                return roles
            }, {})
            return this.spaceInstances.map(instance => ({
                iid: instance.iid,
                instance_name: instance.long_id,
                selectedRole: assignedRoles[instance.iid] || ''
            }))
        },
        fetchUsers() {
            return this.$store.dispatch('spaceStore/fetchSpaceUsers', this.$route.params.sid)
        },
        spaceRole
    },
    async mounted() {
        this.fetchUsers()
        if (!this.currentSpace || this.currentSpace.sid.toString() !== this.$route.params.sid.toString()) {
            await this.$store.dispatch('spaceStore/fetchCurrentSpace')
        }
        this.$store.dispatch('spaceStore/fetchSpaceInstances', this.$route.params.sid)
    }
}
</script>
